import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LoadingOverlay,
  Box,
  Group,
  Title,
  Center,
  Text as MantineText,
  Loader,
} from "@mantine/core";
import { signIn } from "aws-amplify/auth";
import PasswordRequirementInput from "./common/PasswordRequirementInput";
import styles from "./MantineLoginPage.module.css";
import { calculateRoutingDestination } from "../utils/routingUtils";

import { TextInput, Button, Stack, Text } from "@mantine/core";

function MantineLoginPage({ onPageChange }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [routingMessage, setRoutingMessage] = useState("");
  const { state } = useLocation();
  const [formData, setFormData] = useState({
    email: state?.email || "",
    password: "",
    keepSignedIn: false,
    type: "login",
  });

  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
    auth: "",
  });

  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "email" && validateEmail(value)) {
      setFormErrors((prev) => ({
        ...prev,
        email: "",
      }));
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateEmail(formData.email)) {
      setFormErrors((prev) => ({
        ...prev,
        email: "Invalid email address",
      }));
      setIsLoading(false);
      return;
    }

    try {
      const signInResponse = await signIn({
        username: formData.email,
        password: formData.password,
      });

      if (
        signInResponse.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setIsLoading(false);
        navigate("/change-password", {
          state: { challengeResponse: signInResponse },
        });
      } else if (signInResponse.isSignedIn) {
        // Login successful - now calculate where to route
        setIsLoading(false);
        setIsCalculatingRoute(true);
        setRoutingMessage("Setting up your workspace...");

        try {
          const routingResult = await calculateRoutingDestination();

          // Use window.location.href for full page reload to ensure contexts are reset
          window.location.href = routingResult.destination;
        } catch (routingError) {
          console.error("Error calculating route:", routingError);
          // Fallback to calendar on error
          window.location.href = "/admin/calendar";
        }
      }
    } catch (error) {
      console.error("Error signing in:", error);
      setIsLoading(false);
      setIsCalculatingRoute(false);
      setFormErrors((prev) => ({
        ...prev,
        password: error.message,
        auth: error.message,
      }));
    }
  };

  // Show full-screen spinner while calculating routing destination
  if (isCalculatingRoute) {
    return (
      <Box
        className={styles.pageWrapper}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          backgroundColor: "#fff",
        }}
      >
        <Center style={{ flexDirection: "column", gap: "20px" }}>
          <Loader size="lg" type="dots" color="teal" />
          <MantineText size="lg" fw={500} c="dimmed">
            {routingMessage}
          </MantineText>
        </Center>
      </Box>
    );
  }

  return (
    <Box className={styles.pageWrapper}>
      {isLoading && (
        <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
      )}

      {/* Background blobs */}
      <Box className={styles.backgroundBlobs}>
        <Box className={styles.rightBlob} />
      </Box>

      {/* Header with Logo */}
      <header className={styles.header}>
        <img
          src="/images/pagoda-logo-horizontal.png"
          alt="Pagoda"
          className={styles.logo}
        />
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Login Container - wraps title section and form */}
        <Stack gap="lg" className={styles.loginContainer}>
          {/* Title Section */}
          <Stack gap="xs" className={styles.titleSection}>
            <Title order={1} className={styles.title}>
              Welcome back!
            </Title>
            <Text size="sm" c="dimmed" className={styles.subtitle}>
              Don't have an account yet?{" "}
              <Text
                span
                className={styles.subtitleLink}
                onClick={() => onPageChange("signup")}
                style={{ cursor: "pointer" }}
              >
                Sign up now.
              </Text>
            </Text>
          </Stack>

          {/* Form Card */}
          <Box className={styles.formCard}>
            {state?.successMessage && (
              <Text className={styles.successMessage}>
                {state.successMessage}
              </Text>
            )}
            {state?.message && (
              <Text className={styles.successMessage} c="blue">
                {state.message}
              </Text>
            )}
            {formErrors.auth && (
              <Text className={styles.errorMessage}>{formErrors.auth}</Text>
            )}

            <form onSubmit={handleSubmit}>
              <Stack className={styles.formStack}>
                <TextInput
                  label="Email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(event) =>
                    handleInputChange("email", event.currentTarget.value)
                  }
                  size="md"
                  radius="sm"
                  required
                  error={formErrors.email}
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#101820",
                    },
                    input: {
                      "&::placeholder": {
                        color: "#adb5bd",
                      },
                    },
                  }}
                />

                <PasswordRequirementInput
                  label="Password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(event) =>
                    handleInputChange("password", event.currentTarget.value)
                  }
                  size="md"
                  radius="sm"
                  required
                  showRequirements={false}
                />

                <Stack gap="xs">
                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    radius="sm"
                    variant="filled"
                    className={styles.submitButton}
                  >
                    Log In
                  </Button>

                  <Center>
                    <button
                      type="button"
                      onClick={() => onPageChange("forgot-password")}
                      className={styles.forgotPassword}
                    >
                      Forgot password?
                    </button>
                  </Center>
                </Stack>
              </Stack>
            </form>
          </Box>
        </Stack>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text size="sm" className={styles.copyright}>
          © Copyright {new Date().getFullYear()}, Pagoda Labs, Inc.
        </Text>
        <Group gap="md" className={styles.footerLinks}>
          <a
            href="https://pagoda.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy Policy
          </a>
          <a
            href="https://pagoda.co/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Terms & Conditions
          </a>
        </Group>
      </footer>
    </Box>
  );
}

export default MantineLoginPage;
